/**
 * "Count to 10" logic.
 *
 * Two ways to count:
 *  1. Any-time taps: the mother taps whenever she feels a kick, no timer.
 *     We look back over a rolling 2-hour window and tell her whether 10 kicks
 *     have happened within 2 hours.
 *  2. Focused session: she starts a timer and taps along; it stops at 2 hours.
 *
 * The app is a journal, not a detector (DECISIONS D6): it records and shows
 * counts but never judges a count as normal or abnormal. General guidance on
 * when to contact a doctor is always one tap away (showHelp in counting.ts).
 */

export const TARGET_KICKS = 10;
export const WINDOW_MS = 2 * 60 * 60 * 1000;

export interface Completion {
  /** time of the 10th kick */
  at: number;
  /** time of the 1st of those 10 kicks */
  from: number;
  /** minutes it took to reach 10 */
  minutes: number;
}

/**
 * Earliest moment 10 kicks fell inside any 2-hour span, among kicks in [from, to).
 * Sliding window over sorted timestamps: O(n).
 */
export function firstCompletion(times: number[], from: number, to: number): Completion | null {
  const t = times.filter(x => x >= from && x < to).sort((a, b) => a - b);
  for (let j = TARGET_KICKS - 1; j < t.length; j++) {
    const first = t[j - (TARGET_KICKS - 1)]!;
    const tenth = t[j]!;
    if (tenth - first <= WINDOW_MS) {
      return { at: tenth, from: first, minutes: Math.max(1, Math.round((tenth - first) / 60000)) };
    }
  }
  return null;
}

export type WindowState =
  | { kind: 'idle' }                                   // no kicks in the last 2 hours
  | { kind: 'counting'; count: number; msLeft: number; startedAt: number }
  | { kind: 'reached'; completion: Completion };

/**
 * Live status for the home screen.
 * Looks at kicks from the last 2 hours. If 10 are already in there, it's reached.
 * Otherwise: how many so far, and how long until the oldest one drops out of the window.
 */
export function windowState(times: number[], now: number): WindowState {
  const recent = times.filter(x => x > now - WINDOW_MS && x <= now).sort((a, b) => a - b);
  if (!recent.length) return { kind: 'idle' };
  const done = firstCompletion(recent, now - WINDOW_MS, now + 1);
  if (done) return { kind: 'reached', completion: done };
  const startedAt = recent[0]!;
  return { kind: 'counting', count: recent.length, msLeft: Math.max(0, startedAt + WINDOW_MS - now), startedAt };
}

export interface SessionOutcome {
  reachedTarget: boolean;
  minutes: number;
}

export function sessionOutcome(startedAt: number, kickTimes: number[], endedAt: number): SessionOutcome {
  const reachedTarget = kickTimes.length >= TARGET_KICKS;
  return {
    reachedTarget,
    minutes: Math.max(1, Math.round((endedAt - startedAt) / 60000))
  };
}

export function formatDuration(ms: number): { h: number; m: number } {
  const totalMin = Math.ceil(ms / 60000);
  return { h: Math.floor(totalMin / 60), m: totalMin % 60 };
}
